import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/UserContextProvider";
import i18n from "../../../language/i18n";
import { NotificationManager } from "../../../notifications/NotificationManager";
import { BaseModal, Icon, PrimaryButton } from "../../../library";

/**
 * "Etkin olmama sebebiyle bağlantı kesildi" — as a modal, not a toast. Twin of admin-web's
 * `components/modals/SessionInactiveModal`; both consoles had the same defect.
 *
 * The disconnect fires after 30 minutes of inactivity, and it used to announce itself with a toast.
 * By construction nobody ever saw it: the notice is emitted precisely because the learner is not at
 * the screen, and it was gone before they came back. They resumed on an app that was silently
 * receiving nothing — no duel invites, no friend presence, no profile-required-action pushes.
 * (On this side the toast was doubly useless: the local `NotificationManager.info` takes one
 * argument, so the title and the 5000ms duration were dropped at runtime.)
 *
 * A modal is the right shape because it PERSISTS until acknowledged. Three things it must get right:
 *
 *  1. **"Yeniden bağlan" is the ONLY thing that resolves the notice.** Not the socket healing by
 *     itself, and not a stray click. `useWebSocket` listens for mousedown/keypress/scroll/click on
 *     `document` and reconnects on the first one, so the connection frequently comes back before the
 *     learner has read a word — and the notice used to vanish with it, which is exactly the report
 *     this revision answers ("kendi kendine veya mouse click ile düzelmesin"). The whole point of the
 *     modal is to tell someone who was away that they missed things; a notice that clears itself the
 *     instant they touch the mouse never delivers that. So the close is gated on the PRESS: the
 *     effect below closes on "connected" only while a retry the learner asked for is outstanding, and
 *     ESC/backdrop are a no-op. A socket that came back on its own loses nothing by waiting — the
 *     press closes the notice on that same branch, with no round trip, because the status is already
 *     "connected". (The button's own click never reaches the document listener anyway:
 *     `PrimaryButton`'s handler opens with `e.stopPropagation()` and react-modal portals under
 *     `document.body`, which is why the button dispatches the retry explicitly.)
 *  2. **It closes on CONNECTION STATE, not on the click.** Watching the click would flash the modal
 *     away while the socket was still down; watching the state keeps it up, showing
 *     "Yeniden bağlanıyor", until the connection is genuinely back.
 *  3. **Pressing the button ALWAYS resolves the notice** — that is what earns the missing ✕.
 *     Reported from the field: the button was pressed, the modal stayed, and the learner closed it by
 *     hand. Two separate faults produced that:
 *       - the retry itself no-oped. `App`'s handler read `accessToken` out of localStorage and gave
 *         up when it was expired, so on any tab whose token had aged out the status never moved and no
 *         effect here ever ran. (Fixed in App: the token is minted.)
 *       - a retry that reached the wire and FAILED had no outcome of its own. `retried` was released
 *         on a timer so the button came back, but the notice just sat there, and only the ✕ was left.
 *     So every press now has an ending: connected → closed; nothing usable within RETRY_RESOLVE_MS →
 *     closed + told. Note what is NOT an ending — a "disconnected" status. `useWebSocket` sets that
 *     and then retries on its own backoff, so reading it as failure would announce defeat over an
 *     attempt still in flight; the deadline is the only arbiter. That guarantee is what carries the
 *     AVAILABILITY requirement now that the button is the single exit: when the socket backend is
 *     unreachable — a gateway restart, a deploy window, a network that blocks WS — one press still
 *     frees the learner, and every HTTP feature was working the whole time. Closing does not pretend
 *     the socket is back: the left-nav avatar keeps its connection dot (MainLeftNavigation's `qba-ws-`
 *     badge), `useWebSocket` goes on retrying with its own backoff, and the reaper raises this again
 *     on the next idle cycle. That badge is passive, though — this modal's button is the only manual
 *     retry in the app, which is the other reason it must always be pressable.
 *
 * Prefix: sim-
 */

// One deadline covers both jobs: it releases the button AND resolves the notice. It has to exist
// because a status TRANSITION is not guaranteed — `App`'s handler drops the dispatch when no token
// can be minted, and `connectWebSocket` has guards of its own (`isConnectingRef`, a stale connected
// client), so there are paths on which the status never moves and no effect here ever runs. Without
// it those paths left the only affordance disabled for good.
//
// It is also the ONLY arbiter of failure, deliberately. A "disconnected" status is not a verdict:
// `useWebSocket`'s STOMP error handler sets it and THEN arms its own backoff (3 attempts, 3s/6s/12s),
// so treating the first one as final would toast "could not reconnect" over a retry that is still
// running — and the learner would watch the connection come back seconds later. Waiting the deadline
// out covers the first two of those attempts; if a later one succeeds after we have closed, the
// left-nav avatar's dot simply goes green, which is what the toast already promises.
const RETRY_RESOLVE_MS = 8000;

const SessionInactiveModal = () => {
  const {
    webSocketConnectionStatus,
    sessionInactiveNoticeOpen,
    setSessionInactiveNoticeOpen,
  } = useAuth() as any;
  // "idle"      — the notice is just sitting there, nothing asked for.
  // "requested" — the button was pressed; the attempt is in flight (App may first have to mint a fresh
  //               token, a network round trip, and the socket may then handshake and retry more than
  //               once before it is genuinely out of options — see the deadline below).
  const [retryPhase, setRetryPhase] = useState<"idle" | "requested">("idle");
  const resolveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const retryPending = retryPhase !== "idle";

  const clearResolveTimeout = useCallback(() => {
    if (resolveTimeoutRef.current) {
      clearTimeout(resolveTimeoutRef.current);
      resolveTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearResolveTimeout, [clearResolveTimeout]);

  // The socket could not be brought back. Close anyway and SAY so — the learner is at the screen
  // (they just clicked), which is the one moment a toast in this flow is actually read.
  const resolveUnreachable = useCallback(() => {
    clearResolveTimeout();
    setRetryPhase("idle");
    setSessionInactiveNoticeOpen(false);
    NotificationManager.error(i18n.t("session_inactive_reconnect_failed"));
  }, [clearResolveTimeout, setSessionInactiveNoticeOpen]);

  useEffect(() => {
    if (!sessionInactiveNoticeOpen) {
      // Reset for the NEXT notice: this component stays mounted for the whole dashboard session, so a
      // phase left behind here would have the next reap open already-pressed.
      clearResolveTimeout();
      setRetryPhase((phase) => (phase === "idle" ? phase : "idle"));
      return;
    }
    // `retryPending` is half of this condition on purpose — see (1) in the docblock. Without it an
    // auto-reconnect (the document activity listener, the tab-visibility reconnect, useWebSocket's
    // own backoff) closed the notice for the learner, which is the behaviour being removed here.
    if (retryPending && webSocketConnectionStatus === "connected") {
      clearResolveTimeout();
      setRetryPhase("idle");
      setSessionInactiveNoticeOpen(false);
      return;
    }
    // Nothing else is a verdict. "connecting" is progress, and "disconnected" is either the state the
    // reaper left behind or one failed attempt inside useWebSocket's own backoff — neither means the
    // socket is unreachable. The deadline decides.
  }, [
    webSocketConnectionStatus,
    sessionInactiveNoticeOpen,
    retryPending,
    clearResolveTimeout,
    setSessionInactiveNoticeOpen,
  ]);

  if (!sessionInactiveNoticeOpen) return null;

  const handleReconnect = () => {
    setRetryPhase("requested");
    clearResolveTimeout();
    resolveTimeoutRef.current = setTimeout(resolveUnreachable, RETRY_RESOLVE_MS);
    // App.tsx owns the socket lifecycle and listens for this; going through an event keeps a second
    // useWebSocket() instance (and therefore a second socket) from ever being created here. Harmless
    // when the socket already came back on its own: the effect above sees "connected" on this very
    // press and closes.
    window.dispatchEvent(new Event("retryWebSocket"));
  };

  return (
    <BaseModal
      title={i18n.t("session_inactive")}
      isOpen={true}
      size="small"
      onAfterOpen={() => {}}
      // No ✕ — see (3) in the docblock. The button is the modal's own exit now, on every outcome, so
      // the chrome one is not what stands between the learner and an unreachable socket backend.
      hideCloseButton
      // ESC and the backdrop still route here (react-modal ignores `hideCloseButton`), so this stays a
      // deliberate no-op: a backdrop click additionally reaches `useWebSocket`'s document activity
      // listener, and dismissing on it is precisely the "closes itself on a mouse click" behaviour
      // this modal must not have. The button — which always ends, see (3) — is the way out.
      onRequestClose={() => {}}
    >
      <div className="sim-body">
        <div className="sim-icon" aria-hidden>
          <Icon name="warning" size={28} />
        </div>
        <p className="sim-text">{i18n.t("session_inactive_modal_text")}</p>
        {/* Disabled by the LEARNER's own retry only — never by a "connecting" the learner did not
            ask for. The button is the single exit now, and every press of it ends within
            RETRY_RESOLVE_MS; gating it on the status as well would hand a socket that hangs mid-
            handshake (neither callback fires, the status never leaves "connecting") the power to
            disable the exit for good. Pressing during a background attempt is safe: connectWebSocket's
            own `isConnectingRef` guard drops the duplicate, and this press then resolves on whatever
            that attempt does — or on the deadline. */}
        <PrimaryButton
          title={retryPending ? i18n.t("reconnecting") : i18n.t("reconnect")}
          size="medium"
          disabled={retryPending}
          onClick={handleReconnect}
        />
      </div>

      <style>{`
        .sim-body {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          padding: 8px 4px 4px;
          text-align: center;
          font-family: var(--font-sans);
        }
        /* The medallion tokens: a brand disc carrying an icon on a frosted card is exactly what they
           exist for, and they keep the translucency a flat brand-50 fill would lose. */
        .sim-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: var(--medallion-bg);
          border: 1px solid var(--medallion-border);
          color: var(--brand-dark);
        }
        .sim-text {
          font-size: var(--fs-md);
          line-height: 1.55;
          color: var(--text-primary);
          margin: 0;
        }
      `}</style>
    </BaseModal>
  );
};

export default SessionInactiveModal;
